import { Component, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FIKO_DIALOG_STIL } from './fiko-dialog-stil';

/**
 * CATIA Graphics Properties — nesne görsel özellikleri (renk/opaklık/parlaklık/görünüm/görünürlük).
 * element = IOzellik (feature) veya null (gövde/PartBody). Canlı uygulanır.
 */
@Component({
  selector: 'fikoyt-grafik-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="sd" *ngIf="yon">
    <div class="sd-grup">
      <div class="sd-bas">Renk & Görünürlük</div>
      <div class="sd-row"><label>Renk</label>
        <input type="color" class="sd-renk" [ngModel]="renkHex()" (ngModelChange)="setRenk($event)"/>
        <div class="sd-onizleme" [style.background]="renkHex()"></div>
      </div>
      <div class="sd-row"><label>Görünür</label>
        <input type="checkbox" class="sd-chk" [ngModel]="!g().gizli" (ngModelChange)="set('gizli', !$event)"/></div>
      <div class="sd-row"><label>Görünüm</label>
        <select [ngModel]="g().gorunum || 'dolu'" (ngModelChange)="set('gorunum', $event)">
          <option value="dolu">Dolu (Shaded)</option><option value="tel">Tel Kafes (Wireframe)</option>
        </select></div>
    </div>
    <div class="sd-grup">
      <div class="sd-bas">Şeffaflık & Malzeme</div>
      <div class="sd-row"><label>Opaklık</label>
        <input type="range" min="0.05" max="1" step="0.05" [ngModel]="g().opaklik ?? 1" (ngModelChange)="setNum('opaklik',$event)"/>
        <span class="sd-deg">{{ ((g().opaklik ?? 1)*100) | number:'1.0-0' }}%</span></div>
      <div class="sd-row"><label>Metalik</label>
        <input type="range" min="0" max="1" step="0.05" [ngModel]="g().metalik ?? varMetalik" (ngModelChange)="setNum('metalik',$event)"/>
        <span class="sd-deg">{{ ((g().metalik ?? varMetalik)*100) | number:'1.0-0' }}%</span></div>
      <div class="sd-row"><label>Pürüz</label>
        <input type="range" min="0" max="1" step="0.05" [ngModel]="g().puruz ?? varPuruz" (ngModelChange)="setNum('puruz',$event)"/>
        <span class="sd-deg">{{ ((g().puruz ?? varPuruz)*100) | number:'1.0-0' }}%</span></div>
    </div>
    @if(!element){
      <div class="sd-grup">
        <div class="sd-bas">Kenarlar (Katı)</div>
        <div class="sd-row"><label>Kalınlık</label>
          <input type="range" min="1" max="6" step="0.5" [ngModel]="g().kenarKalinlik ?? 2" (ngModelChange)="setNum('kenarKalinlik',$event)"/>
          <span class="sd-deg">{{ g().kenarKalinlik ?? 2 }}px</span></div>
        <div class="sd-row"><label>Kenar Rengi</label>
          <input type="color" class="sd-renk" [ngModel]="kenarHex()" (ngModelChange)="setKenarRenk($event)"/></div>
      </div>
    }
    <div class="sd-grup">
      <div class="sd-row"><label>Hızlı</label>
        <div class="sd-hizli">
          @for(c of paletRenkler; track c){ <button class="sd-pal" [style.background]="'#'+c.toString(16).padStart(6,'0')" (click)="setRenkNum(c)"></button> }
        </div></div>
    </div>
    <button class="sd-btn sd-sec" (click)="tumuneUygula()" title="Bu görünümü gövde + tüm yüzey/eğrilere uygula">⊕ Tümüne Uygula</button>
    <div class="sd-altbar"><button class="sd-btn sd-ok" (click)="tamam()">✔ Tamam</button><button class="sd-btn" (click)="sifirla()">↺ Sıfırla</button></div>
  </div>`,
  styles: [FIKO_DIALOG_STIL + `
    .sd-renk{width:42px;height:26px;border:1px solid rgba(56,189,248,.4);border-radius:6px;background:none;cursor:pointer;padding:0;}
    .sd-onizleme{flex:1;height:18px;border-radius:5px;margin-left:6px;border:1px solid rgba(255,255,255,.15);}
    .sd-chk{accent-color:#38bdf8;width:16px;height:16px;cursor:pointer;}
    .sd-deg{min-width:38px;text-align:right;font-size:11px;opacity:.8;}
    .sd-row input[type=range]{flex:1;accent-color:#38bdf8;cursor:pointer;}
    .sd-hizli{display:flex;gap:5px;flex-wrap:wrap;flex:1;}
    .sd-pal{width:22px;height:22px;border-radius:5px;border:1px solid rgba(255,255,255,.2);cursor:pointer;transition:transform .12s;}
    .sd-pal:hover{transform:scale(1.18);}
  `]
})
export class FikoGrafikDialog {
  @Input() element: any;          // IOzellik veya null (gövde)
  @Input() yonetici: any;
  @Input() pencere: any;
  varMetalik = 0.15; varPuruz = 0.6;
  paletRenkler = [0xc8c8c8, 0x2dd4bf, 0x38bdf8, 0xfacc15, 0xf87171, 0x4ade80, 0xa78bfa, 0xfb923c, 0x94a3b8, 0x1e293b];
  get yon() { return this.yonetici; }
  constructor(public CD: ChangeDetectorRef) {}
  g(): any { return (this.element ? (this.element.grafik ||= {}) : this.yonetici?.govdeGrafik) || {}; }
  renkHex(): string {
    const r = this.g().renk ?? (this.element ? 0x22d3ee : (this.yonetici?.aktifMalzeme?.renk ?? 0x9aa6b2));
    return '#' + (r & 0xffffff).toString(16).padStart(6, '0');
  }
  kenarHex(): string { const r = this.g().kenarRenk ?? 0x0b1119; return '#' + (r & 0xffffff).toString(16).padStart(6, '0'); }
  setKenarRenk(hex: string): void { this.set('kenarRenk', parseInt(hex.replace('#', ''), 16)); }
  setRenk(hex: string): void { this.setRenkNum(parseInt(hex.replace('#', ''), 16)); }
  setRenkNum(n: number): void { this.set('renk', n); }
  set(a: string, v: any): void { this.yonetici?.grafikGuncelle(this.element, a, v); this.CD.markForCheck(); }
  setNum(a: string, v: any): void { const n = +v; if (!isNaN(n)) this.set(a, n); }
  private kapat(): void { (this.pencere?.instance || this.pencere)?.kapa?.(); }
  tamam(): void { this.kapat(); }
  tumuneUygula(): void { this.yonetici?.grafikTumuneUygula?.({ ...this.g() }); this.CD.markForCheck(); }
  sifirla(): void { this.yonetici?.grafikSifirla(this.element); this.CD.markForCheck(); }
}
